import {
  CAMERA_TYPE,
  type ThirdPersonCamera,
} from "@/shared/state/visualization/schema/v1/three-d";

/**
 * A point, as three plain numbers.
 *
 * Plain rather than a `babylon.Vector3` on purpose: this module holds the
 * framing *policy*, and the policy is arithmetic on a box. Keeping it free of
 * Babylon lets a caller that has no scene — panel state, a reducer, a test —
 * derive the same camera as the one that measured a real model.
 *
 * A `babylon.Vector3` satisfies this structurally, so a caller that does have
 * one passes it straight through.
 */
export interface Point3 {
  x: number;
  y: number;
  z: number;
}

/**
 * How big a model is, and where it sits.
 *
 * `extent` is the largest of the bounding box's three dimensions, in metres —
 * URDF is strict SI and the `ROBOTO_device` extension declares
 * `units: {length: "m"}`, so one world unit is one metre and no conversion is
 * involved. `center` is the box's midpoint, which is what the camera should look
 * at: a robot's origin is its floor mount, so framing that puts the machine in
 * the top half of the viewport.
 */
export interface ModelBounds {
  extent: number;
  center: Point3;
}

/**
 * Extent used when a model's bounds cannot be measured.
 *
 * One metre, because that is the order of magnitude of everything this platform
 * ingests (measured: 0.20m for `joint_zoo`, 0.77m for `sample_robot`, 1.00m for
 * `gantry_gripper`, 1.12m for a Franka Panda). Reached by a model whose nodes
 * carry no geometry at all — a kinematics-only export, or a test fixture — where
 * every alternative is worse: a zero extent gives a zero camera radius and a
 * zero grid spacing, and both are degenerate rather than merely wrong.
 */
export const FALLBACK_EXTENT_M = 1;

/**
 * Below this an extent is treated as unmeasurable rather than as very small.
 *
 * A tenth of a millimetre. Nothing real is this small, so a value under it means
 * empty bounds or a numerical accident, not a tiny robot.
 */
const DEGENERATE_EXTENT_M = 1e-4;

/**
 * Camera distance as a multiple of the model's largest extent.
 *
 * At the panel's 55° vertical FOV, the visible height at distance `d` is
 * `2·d·tan(27.5°) ≈ 1.04·d`. So a radius of `1.75·extent` shows about `1.8`
 * extents of height and the robot fills a bit over half the frame — framed, with
 * room to swing a joint through without it leaving the viewport.
 *
 * For contrast, the constants this replaced (the 3D panel's own orbit defaults,
 * tuned for vehicle trajectories hundreds of metres across) put the camera 20.8m
 * from a 0.77m robot: about 4% of frame height.
 */
export const MODEL_FRAMING_RADIUS_PER_EXTENT = 1.75;

/**
 * A three-quarter view of the robot's **front**, from slightly above the
 * horizon.
 *
 * Babylon's `ArcRotateCamera` sits at
 * `target + r·(cos α·sin β, cos β, sin α·sin β)`, so `alpha` chooses which side
 * of the machine the camera stands on. Which side that *is* takes two frame
 * changes to work out, and skipping either gets the answer backwards — this
 * constant was 45° for exactly that reason, and 45° is the robot's rear:
 *
 * 1. **The converter rotates z-up onto y-up.** `zup_to_yup()` in
 *    `packages/roboto/src/roboto/urdf/converter.py` is a −90° rotation about X,
 *    so URDF `(x, y, z)` becomes glTF `(x, z, −y)`. Under REP-103 — X forward,
 *    Y left, Z up — the robot's forward axis lands on glTF **+X**, its up on
 *    **+Y** and its right on **+Z**.
 * 2. **Babylon's glTF loader mirrors the document.** The synthetic `__root__`
 *    node it inserts carries a world matrix of `diag(−1, 1, 1)` — a 180° turn
 *    about Y composed with a negative Z scale, which is a *reflection* and not a
 *    rotation. So glTF `(x, y, z)` is drawn at Babylon `(−x, y, z)`, and the
 *    robot's forward axis points along Babylon **−X**.
 *
 * `alpha` 135° therefore puts the camera at `(−0.664r, +0.342r, +0.664r)`:
 * off the robot's front-right corner, where depth reads and a planar joint's
 * swing is visible rather than edge-on. At 45° the camera stood at `+X` — behind
 * the robot, looking at its back. That is why the panel's recenter control reads
 * these constants rather than the orbit defaults: it used to re-apply 45°, and
 * one click undid every framing on this page.
 *
 * `beta` is measured from +Y, so 70° is 20° above the horizon — high enough to
 * see the floor grid for scale, low enough to keep a robot's own height legible.
 *
 * **This is right for conforming robots, not right by construction.** REP-103 is
 * a convention; a URDF is free to point forward along any axis it likes, and one
 * that does gets whatever view its own frame implies.
 * `modelFraming.browser.test.ts` measures step 2 off a real load rather than
 * restating it, because that is the step that is easy to get wrong and
 * impossible to spot by reading.
 *
 * Both named, rather than left as bare numbers at the call sites, to match
 * orbit's `ORBIT_DEFAULT_ALPHA_RAD`/`ORBIT_DEFAULT_BETA_RAD`.
 */
export const MODEL_FRAMING_ALPHA_RAD = (135 * Math.PI) / 180;
export const MODEL_FRAMING_BETA_RAD = (70 * Math.PI) / 180;

/**
 * The size and centre of the box with these two corners.
 *
 * The one place the "unmeasurable" rules live, so a caller measuring a loaded
 * document and a caller reading a live mesh's world corners agree about what a
 * degenerate box means. A box that is a single point, a flat plane, or a NaN
 * from a corrupt accessor yields {@link FALLBACK_EXTENT_M} — its centre is still
 * meaningful and is kept — and a centre that is not a real point falls back to
 * the origin.
 */
export function modelBoundsOf(min: Point3, max: Point3): ModelBounds {
  const extent = Math.max(max.x - min.x, max.y - min.y, max.z - min.z);
  const center = {
    x: (min.x + max.x) / 2,
    y: (min.y + max.y) / 2,
    z: (min.z + max.z) / 2,
  };
  return {
    extent:
      Number.isFinite(extent) && extent >= DEGENERATE_EXTENT_M
        ? extent
        : FALLBACK_EXTENT_M,
    center: isFinitePoint(center) ? center : { x: 0, y: 0, z: 0 },
  };
}

/** Whether every coordinate is a real number. */
function isFinitePoint(point: Point3): boolean {
  return (
    Number.isFinite(point.x) &&
    Number.isFinite(point.y) &&
    Number.isFinite(point.z)
  );
}

/**
 * The camera that frames a model of these bounds.
 *
 * Third person because the callers are the ones with **no entity to track**: the
 * ingest wizard's preview scene, which holds one model and no data, and the 3D
 * panel's recenter control, which is itself third-person-only. A third-person
 * camera's focus is a fixed world point, which is exactly right for a model that
 * is not going anywhere.
 *
 * This is deliberately *not* what a model import does to a live panel. It used
 * to be: an import measured the file and the reducer adopted the result, which
 * framed the robot by switching the panel out of orbit — and so gave up
 * tracking, leaving a base bound to an odometry topic to drive out of frame. An
 * import no longer touches the camera; an orbit camera can be pointed at one
 * node of the model instead (`camera.target.node`), which frames a robot without
 * giving up the following.
 *
 * What orbit still cannot express is the rest of this: its spec carries a
 * tracked entity and a zoom envelope and nothing else — no radius (one is
 * derived at construction as `zoom.min + (zoom.max − zoom.min)/5`, which against
 * the default envelope is 20.8m whatever the target's size) and no alpha/beta.
 * Closing that gap is a schema change plus a live-apply path, not a different
 * call here.
 *
 * No zoom envelope is chosen here, because there is no field for one and none is
 * needed: `computeThirdPersonZoom` derives it from the union of the scene's
 * bounding boxes — `[max(0.5, r·0.1), r·41]` for a scene of bounding radius `r`
 * — which for a robot of this size already brackets the radius below.
 */
export function framingCamera(bounds: ModelBounds): ThirdPersonCamera {
  const { center } = bounds;
  return {
    type: CAMERA_TYPE.thirdPerson,
    focusPoint: { x: center.x, y: center.y, z: center.z },
    alpha: MODEL_FRAMING_ALPHA_RAD,
    beta: MODEL_FRAMING_BETA_RAD,
    radius: bounds.extent * MODEL_FRAMING_RADIUS_PER_EXTENT,
  };
}
